/**
 * Team tab — PR F.
 *
 * Lists project members and linked chats, with three actions:
 *   - 🔗 Invite teammate  → mint invite link, show modal w/ Copy
 *   - + Add contact       → form (@handle + display_name + role + kind)
 *   - ❌ remove member    → confirm + DELETE (disabled on self)
 *
 * Reads members from a per-tab `useQuery` rather than from the parent
 * `ProjectDetail` so removals/additions can refresh just this slice
 * without re-pulling notes/tasks/decisions/risks.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../api/client";
import { getProjectDetail } from "../api/projectMemory";
import {
  attachContactToProject,
  createProjectInvite,
  listProjectMembers,
  removeProjectMember,
} from "../api/teamManagement";
import { haptic } from "../hooks/useHaptic";
import type {
  ContactCreateRequest,
  ContactKind,
  InviteResponse,
  ProjectMemberRow,
} from "../lib/types";
import { EmptyState } from "./EmptyState";
import { LoadingScreen } from "./LoadingScreen";
import { RelativeTime } from "./RelativeTime";

interface Props {
  projectId: string;
}

const CONTACT_KINDS: ReadonlyArray<{ value: ContactKind; label: string }> = [
  { value: "client", label: "Client" },
  { value: "team_lead", label: "Team lead" },
  { value: "external", label: "External" },
  { value: "other", label: "Other" },
];

export function TeamTab({ projectId }: Props): React.ReactElement {
  const queryClient = useQueryClient();

  // Members + linked_chats both ship inside ProjectDetail; the tab
  // refetches detail (not just members) on mutations so the tab-bar
  // counter in the parent also re-renders.
  const detail = useQuery({
    queryKey: ["project", projectId],
    queryFn: ({ signal }) => getProjectDetail(projectId, signal),
  });
  const members = useQuery<ProjectMemberRow[]>({
    queryKey: ["project", projectId, "members"],
    queryFn: ({ signal }) => listProjectMembers(projectId, signal),
  });

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["project", projectId, "members"],
      }),
      queryClient.invalidateQueries({ queryKey: ["project", projectId] }),
    ]);
  };

  // --- mutations ---

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => removeProjectMember(projectId, memberId),
    onSuccess: async () => {
      haptic.notify("success");
      await invalidate();
    },
    onError: () => haptic.notify("error"),
  });

  const inviteMutation = useMutation({
    mutationFn: () => createProjectInvite(projectId),
    onSuccess: () => haptic.notify("success"),
    onError: () => haptic.notify("error"),
  });

  const contactMutation = useMutation({
    mutationFn: (body: ContactCreateRequest) =>
      attachContactToProject(projectId, body),
    onSuccess: async () => {
      haptic.notify("success");
      setShowContactForm(false);
      await invalidate();
    },
    onError: () => haptic.notify("error"),
  });

  // --- local UI state ---

  const [showContactForm, setShowContactForm] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  if (members.isLoading || detail.isLoading) {
    return <LoadingScreen message="Loading team…" />;
  }

  const rows = members.data ?? [];
  const linkedChats = detail.data?.linked_chats ?? [];

  return (
    <div className="space-y-4">
      {/* --- Members --- */}
      <section>
        <Header
          title={`Members (${rows.length})`}
          action={
            <PrimaryBtn
              onClick={() => {
                haptic.impact("light");
                inviteMutation.mutate();
              }}
              disabled={inviteMutation.isPending}
            >
              🔗 Invite
            </PrimaryBtn>
          }
        />

        {inviteMutation.data ? (
          <InviteCard invite={inviteMutation.data} />
        ) : null}
        {inviteMutation.isError ? (
          <ErrText>{describe(inviteMutation.error)}</ErrText>
        ) : null}

        {rows.length === 0 ? (
          <EmptyState
            title="No members yet"
            hint="Tap 🔗 Invite to get a link, or + Add contact for an external party."
          />
        ) : (
          <ul className="space-y-1.5">
            {rows.map((m) => (
              <li key={m.id}>
                <MemberRow
                  member={m}
                  isRemoving={
                    removeMutation.isPending &&
                    removeMutation.variables === m.id
                  }
                  confirmOpen={confirmRemoveId === m.id}
                  onAskRemove={() => setConfirmRemoveId(m.id)}
                  onCancelRemove={() => setConfirmRemoveId(null)}
                  onConfirmRemove={() => {
                    setConfirmRemoveId(null);
                    removeMutation.mutate(m.id);
                  }}
                />
              </li>
            ))}
          </ul>
        )}
        {removeMutation.isError ? (
          <ErrText>{describe(removeMutation.error)}</ErrText>
        ) : null}

        <div className="mt-3">
          {showContactForm ? (
            <ContactForm
              isSubmitting={contactMutation.isPending}
              error={
                contactMutation.isError
                  ? describe(contactMutation.error)
                  : null
              }
              onSubmit={(body) => contactMutation.mutate(body)}
              onCancel={() => setShowContactForm(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowContactForm(true)}
              className="text-[12px] px-3 py-1.5 rounded-full"
              style={{
                background: "var(--color-secondary-bg)",
                color: "var(--color-text)",
              }}
            >
              + Add contact
            </button>
          )}
        </div>
      </section>

      {/* --- Linked chats --- */}
      <section>
        <Header title={`Linked chats (${linkedChats.length})`} />
        {linkedChats.length === 0 ? (
          <EmptyState
            title="No chats linked"
            hint="Add the bot to a group and pick this project from the prompt to capture conversations passively."
          />
        ) : (
          <ul className="space-y-1.5">
            {linkedChats.map((c) => (
              <li
                key={c.id}
                className="rounded-xl p-3"
                style={{ background: "var(--color-secondary-bg)" }}
              >
                <div className="text-[13px] font-medium">
                  {c.chat_title ?? "(untitled)"}
                </div>
                <div
                  className="text-[11px] mt-0.5"
                  style={{ color: "var(--color-hint)" }}
                >
                  linked <RelativeTime iso={c.linked_at} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ---------- bits ---------- */

function Header({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between mb-2">
      <h3
        className="text-[11px] uppercase tracking-wider"
        style={{ color: "var(--color-hint)" }}
      >
        {title}
      </h3>
      {action}
    </div>
  );
}

function PrimaryBtn(props: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      className="text-[12px] px-3 py-1.5 rounded-full disabled:opacity-40 active:scale-95 transition-transform"
      style={{
        background: "var(--color-button)",
        color: "var(--color-button-text)",
      }}
    >
      {props.children}
    </button>
  );
}

function ErrText({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <p
      className="text-xs mt-2"
      style={{ color: "var(--color-destructive)" }}
      role="alert"
    >
      {children}
    </p>
  );
}

function MemberRow({
  member,
  isRemoving,
  confirmOpen,
  onAskRemove,
  onCancelRemove,
  onConfirmRemove,
}: {
  member: ProjectMemberRow;
  isRemoving: boolean;
  confirmOpen: boolean;
  onAskRemove: () => void;
  onCancelRemove: () => void;
  onConfirmRemove: () => void;
}): React.ReactElement {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: "var(--color-secondary-bg)" }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium truncate">
            {member.display_name}
            {member.is_self ? (
              <span
                className="ml-1.5 text-[10px] uppercase tracking-wider"
                style={{ color: "var(--color-hint)" }}
              >
                you
              </span>
            ) : null}
          </div>
          <div
            className="text-[11px] mt-0.5 flex flex-wrap gap-x-2"
            style={{ color: "var(--color-hint)" }}
          >
            {member.username ? <span>@{member.username}</span> : null}
            {member.role ? <span>{member.role}</span> : null}
          </div>
        </div>
        {member.is_self ? (
          <span
            className="text-[10px] px-2 py-1 rounded-full whitespace-nowrap"
            style={{ color: "var(--color-hint)" }}
            title="You can't remove yourself"
          >
            —
          </span>
        ) : confirmOpen ? (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={onConfirmRemove}
              disabled={isRemoving}
              className="text-[11px] px-2 py-1 rounded-full"
              style={{
                background: "var(--color-destructive)",
                color: "var(--color-button-text)",
              }}
            >
              {isRemoving ? "…" : "Remove"}
            </button>
            <button
              type="button"
              onClick={onCancelRemove}
              className="text-[11px] px-2 py-1 rounded-full"
              style={{
                background: "var(--color-bg)",
                color: "var(--color-text)",
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onAskRemove}
            className="text-[11px] px-2 py-1 rounded-full"
            style={{
              background: "var(--color-bg)",
              color: "var(--color-destructive)",
            }}
            aria-label={`Remove ${member.display_name}`}
          >
            ❌
          </button>
        )}
      </div>
    </div>
  );
}

function InviteCard({
  invite,
}: {
  invite: InviteResponse;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);
  return (
    <div
      className="rounded-xl p-3 mb-2"
      style={{ background: "var(--color-secondary-bg)" }}
    >
      <div
        className="text-[11px] uppercase tracking-wider mb-1"
        style={{ color: "var(--color-hint)" }}
      >
        Invite link
      </div>
      <code
        className="block text-[11px] break-all leading-snug px-2 py-1.5 rounded"
        style={{ background: "var(--color-bg)" }}
      >
        {invite.url}
      </code>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(invite.url);
            haptic.impact("light");
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
          className="text-[12px] px-3 py-1 rounded-full"
          style={{
            background: "var(--color-button)",
            color: "var(--color-button-text)",
          }}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
        {invite.expires_at ? (
          <span
            className="text-[11px] self-center"
            style={{ color: "var(--color-hint)" }}
          >
            valid for ~30 days
          </span>
        ) : null}
      </div>
    </div>
  );
}

function ContactForm({
  isSubmitting,
  error,
  onSubmit,
  onCancel,
}: {
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (body: ContactCreateRequest) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [role, setRole] = useState("");
  const [kind, setKind] = useState<ContactKind>("external");

  const canSubmit = name.trim().length > 0 && handle.trim().length > 0;

  const submit = (): void => {
    if (!canSubmit) return;
    onSubmit({
      display_name: name.trim(),
      telegram_username: handle.trim().replace(/^@/, ""),
      role: role.trim() || null,
      kind,
    });
  };

  return (
    <div
      className="rounded-xl p-3 space-y-2"
      style={{ background: "var(--color-secondary-bg)" }}
    >
      <div
        className="text-[11px] uppercase tracking-wider"
        style={{ color: "var(--color-hint)" }}
      >
        Add contact
      </div>
      <Input value={name} onChange={setName} placeholder="Display name" />
      <Input
        value={handle}
        onChange={setHandle}
        placeholder="@telegram_handle"
      />
      <Input
        value={role}
        onChange={setRole}
        placeholder="Role (optional, e.g. Designer)"
      />
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value as ContactKind)}
        className="w-full rounded-lg px-3 py-2 text-sm outline-none"
        style={{
          background: "var(--color-bg)",
          color: "var(--color-text)",
          border: "1px solid var(--color-secondary-bg)",
        }}
      >
        {CONTACT_KINDS.map((k) => (
          <option key={k.value} value={k.value}>
            {k.label}
          </option>
        ))}
      </select>
      {error ? <ErrText>{error}</ErrText> : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit || isSubmitting}
          className="text-[12px] px-3 py-1.5 rounded-full disabled:opacity-40"
          style={{
            background: "var(--color-button)",
            color: "var(--color-button-text)",
          }}
        >
          {isSubmitting ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-[12px] px-3 py-1.5 rounded-full"
          style={{
            background: "var(--color-bg)",
            color: "var(--color-text)",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Input(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): React.ReactElement {
  return (
    <input
      type="text"
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      className="w-full rounded-lg px-3 py-2 text-sm outline-none"
      style={{
        background: "var(--color-bg)",
        color: "var(--color-text)",
        border: "1px solid transparent",
      }}
    />
  );
}

function describe(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Unknown error";
}
